import { BarList, DataTable, EmptyState, SectionCard, StatTile, fmtMoney, fmtNum, fmtPct, type Column } from "@/components/reports/ReportWidgets";
import { useReport } from "@/components/reports/DateRangeFilter";
import { Status, q, type TabProps } from "./ReportTabs";
import type { TextileQtyRow, TextileSalesReport, TextileShareRow } from "@/types/report";
import { DonutPieChart } from "@/components/charts/DonutPieChart";
import { RadialTargetGauge } from "@/components/charts/RadialTargetGauge";

const qtyColumns = (first: string): Column<TextileQtyRow>[] => [
  { key: "label", header: first, render: (r) => <span className="font-medium text-foreground">{r.label}</span> },
  { key: "unit", header: "Unit" },
  { key: "quantity", header: "Quantity", align: "right", render: (r) => fmtNum(r.quantity) },
  { key: "value", header: "Value (before GST)", align: "right", render: (r) => fmtMoney(r.value) },
  { key: "orders", header: "Orders", align: "right" },
];

const shareColumns = (first: string): Column<TextileShareRow>[] => [
  { key: "label", header: first, render: (r) => <span className="font-medium text-foreground">{r.label}</span> },
  { key: "buyers", header: "Buyers", align: "right" },
  { key: "orders", header: "Orders", align: "right" },
  { key: "value", header: "Value", align: "right", render: (r) => fmtMoney(r.value) },
  { key: "share_pct", header: "Share", align: "right", render: (r) => fmtPct(r.share_pct) },
];

/** Textile sales: what was sold (by type and category, per unit), to whom, through whom, and how dispatch and complaints stand */
export function TextileTab({ range, headId }: TabProps) {
  const { data, loading, error } = useReport<TextileSalesReport>(`/api/Report/TextileSales${q(range, headId)}`);
  const d = data;
  const onTimeLines = Math.max(0, (d?.dispatch.pending_lines || 0) - (d?.dispatch.overdue_lines || 0));
  const totalPending = Math.max(1, d?.dispatch.pending_lines || 0);

  return (
    <Status loading={loading} error={error}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatTile label="Orders" value={fmtNum(d?.totals.orders || 0)} />
          <StatTile label="Order value" value={fmtMoney(d?.totals.value || 0)} hint="Before GST" />
          <StatTile label="Buyers" value={fmtNum(d?.totals.buyers || 0)} />
          <StatTile label="Days to first dispatch" value={d?.dispatch.avg_days_to_first_dispatch == null ? "-" : String(d.dispatch.avg_days_to_first_dispatch)} hint="From PO to first consignment" />
        </div>

        <SectionCard title="Dispatch fulfillment" description="Open orders delivery pacing as of today">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
            <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                <p className="text-xs text-muted-foreground font-medium">Not started</p>
                <p className="text-xl font-bold text-foreground mt-1">{d?.dispatch.orders_not_started ?? 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                <p className="text-xs text-muted-foreground font-medium">Partly dispatched</p>
                <p className="text-xl font-bold text-foreground mt-1">{d?.dispatch.orders_partial ?? 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                <p className="text-xs text-muted-foreground font-medium">Pending lines</p>
                <p className="text-xl font-bold text-foreground mt-1">{d?.dispatch.pending_lines ?? 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                <p className="text-xs text-muted-foreground font-medium">Past delivery date</p>
                <p className={`text-xl font-bold mt-1 ${d && d.dispatch.overdue_lines > 0 ? "text-red-600" : "text-foreground"}`}>{d?.dispatch.overdue_lines ?? 0}</p>
              </div>
            </div>
            <div className="flex justify-center border-t md:border-t-0 md:border-l border-slate-100 pt-3 md:pt-0">
              <RadialTargetGauge
                achieved={onTimeLines}
                target={totalPending}
                label="On-Time Rate"
                formatMoney={(v) => `${v} lines`}
                size={140}
              />
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Quantity sold by product type" description="Metres, kilos and pieces are kept apart, so each unit has its own row">
          <DataTable columns={qtyColumns("Product type")} rows={d?.by_product_type || []} rowKey={(r) => `${r.code || r.label}-${r.unit}`} defaultSort={{ key: "value", dir: "desc" }} emptyMessage="No orders in this period." />
        </SectionCard>

        <SectionCard title="Quantity sold by category">
          <DataTable columns={qtyColumns("Category")} rows={d?.by_category || []} rowKey={(r) => `${r.label}-${r.unit}`} defaultSort={{ key: "value", dir: "desc" }} emptyMessage="No orders in this period." />
        </SectionCard>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <SectionCard title="Sales by buyer type" description="Revenue breakdown by buyer category">
            <DonutPieChart
              data={(d?.by_buyer_type || []).filter((r) => r.value > 0).map((r) => ({
                label: r.label,
                value: r.value,
                sublabel: `${r.buyers} buyers`,
              }))}
              formatValue={(v) => fmtMoney(v)}
              centerValue={fmtMoney((d?.by_buyer_type || []).reduce((sum, r) => sum + r.value, 0))}
              centerLabel="Buyer Sales"
              donut={true}
            />
          </SectionCard>
          <SectionCard title="Sales by market" description="Revenue concentration by buyer city">
            <DonutPieChart
              data={(d?.by_market || []).filter((r) => r.value > 0).map((r) => ({
                label: r.label,
                value: r.value,
                sublabel: `${r.orders} orders`,
              }))}
              formatValue={(v) => fmtMoney(v)}
              centerValue={fmtMoney((d?.by_market || []).reduce((sum, r) => sum + r.value, 0))}
              centerLabel="Market Sales"
              donut={true}
            />
          </SectionCard>
        </div>

        <SectionCard title="Business and commission by agent" description="Commission is each order's value times the rate agreed on its deal">
          <DataTable
            columns={[
              { key: "label", header: "Agent", render: (r) => <span className="font-medium text-foreground">{r.label}</span> },
              { key: "orders", header: "Orders", align: "right" },
              { key: "value", header: "Business", align: "right", render: (r) => fmtMoney(r.value) },
              { key: "share_pct", header: "Share", align: "right", render: (r) => fmtPct(r.share_pct) },
              { key: "commission", header: "Commission", align: "right", render: (r) => (r.agent_id ? fmtMoney(r.commission, false) : "—") },
            ]}
            rows={d?.by_agent || []}
            rowKey={(r) => r.agent_id || "direct"}
            defaultSort={{ key: "value", dir: "desc" }}
            emptyMessage="No orders in this period."
          />
        </SectionCard>

        <SectionCard title="Complaints in the period" description="By type, with what buyers claimed">
          {(d?.complaints || []).length === 0 ? (
            <EmptyState message="No complaints in this period." />
          ) : (
            <DataTable
              columns={[
                { key: "label", header: "Type", render: (r) => <span className="font-medium text-foreground">{r.label}</span> },
                { key: "count", header: "Complaints", align: "right" },
                { key: "open", header: "Still open", align: "right" },
                { key: "claimed", header: "Claimed", align: "right", render: (r) => fmtMoney(r.claimed, false) },
              ]}
              rows={d?.complaints || []}
              rowKey={(r) => r.code}
              defaultSort={{ key: "count", dir: "desc" }}
            />
          )}
        </SectionCard>
      </div>
    </Status>
  );
}
